import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

/**
 * A small panel anchored to the control that opened it — the shape both
 * the Appearance and the Help controls take (4.1.56).
 *
 * WHY IT IS `position: fixed` AND RENDERED INTO <body>. The places these
 * controls live all clip: the homepage is `overflow: hidden`, the
 * console's sidebar scrolls, and a footer sits at the bottom of the
 * viewport. An absolutely positioned panel inside any of them is cut
 * off exactly where it matters. Measured against the viewport instead,
 * it can be placed so it is NEVER cut off: below the anchor when there
 * is room, above it when there is not, and slid sideways to stay inside
 * an 8px margin. It is measured again on resize and scroll.
 *
 * WHAT IT DOES FOR THE KEYBOARD. Escape closes it. Tab cycles inside it
 * (the panel is appended to the end of the document, so tabbing out of
 * it would otherwise land somewhere surprising). Up and Down move
 * between its controls. Focus lands on the first control — or the one
 * marked `data-autofocus` — on open and goes back to the anchor on
 * close, unless the close was a click somewhere else, in which case
 * the click keeps its focus.
 */
export interface PopoverProps {
  open: boolean;
  onClose: () => void;
  /** The control the panel is anchored to; also the one focus returns to. */
  anchorRef: RefObject<HTMLElement | null>;
  /** Preferred side. It flips when the preferred side has no room. */
  placement?: 'bottom' | 'top';
  /** Which edge of the anchor the panel lines up with. */
  align?: 'start' | 'end';
  /** The dialog's accessible name. */
  label: string;
  id?: string;
  className?: string;
  children: ReactNode;
}

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
const MARGIN = 8;
const GAP = 8;

export function Popover({ open, onClose, anchorRef, placement = 'bottom', align = 'end', label, id, className, children }: PopoverProps) {
  const panel = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; side: 'top' | 'bottom' } | null>(null);
  // Callers pass inline closures; keep the latest without re-running the effects.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // Whether focus goes back to the anchor when the panel closes. A click
  // elsewhere on the page keeps the focus it gave; everything else
  // (Escape, a choice made, the anchor pressed again) hands it back.
  const restoreFocus = useRef(true);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const place = () => {
      const a = anchorRef.current;
      const p = panel.current;
      if (!a || !p) return;
      const r = a.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      p.style.maxWidth = `${vw - MARGIN * 2}px`;
      p.style.maxHeight = `${vh - MARGIN * 2}px`;
      const w = p.offsetWidth;
      const h = p.offsetHeight;
      let left = align === 'end' ? r.right - w : r.left;
      left = Math.max(MARGIN, Math.min(left, vw - MARGIN - w));
      const below = r.bottom + GAP;
      const above = r.top - GAP - h;
      const fitsBelow = below + h <= vh - MARGIN;
      const fitsAbove = above >= MARGIN;
      let side: 'top' | 'bottom';
      if (placement === 'bottom') side = fitsBelow || !fitsAbove ? 'bottom' : 'top';
      else side = fitsAbove || !fitsBelow ? 'top' : 'bottom';
      let top = side === 'bottom' ? below : above;
      top = Math.max(MARGIN, Math.min(top, vh - MARGIN - h));
      setPos({ top, left, side });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef, placement, align]);

  useEffect(() => {
    if (!open) return;
    restoreFocus.current = true;
    // The panel is measured (and made visible) by the layout effect
    // before this runs, so the first control can take focus. It is never
    // `visibility: hidden` while unmeasured — a hidden element refuses
    // focus, and the focus effect can run before the positioned render.
    const el = panel.current;
    const preferred = el?.querySelector<HTMLElement>('[data-autofocus]');
    const first = preferred ?? el?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? el)?.focus();

    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (panel.current?.contains(t) || anchorRef.current?.contains(t)) return;
      restoreFocus.current = false;
      closeRef.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      const inside = panel.current && panel.current.contains(document.activeElement);
      if (!inside || !panel.current) return;
      const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((n) => n.offsetParent !== null || n === document.activeElement);
      if (items.length === 0) return;
      const at = items.indexOf(document.activeElement as HTMLElement);
      if (e.key === 'Tab') {
        if (e.shiftKey && (at <= 0)) { e.preventDefault(); items[items.length - 1]!.focus(); }
        else if (!e.shiftKey && at === items.length - 1) { e.preventDefault(); items[0]!.focus(); }
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        // A control that handles its own arrows (the theme radios) stops
        // the event before it gets here.
        e.preventDefault();
        const next = e.key === 'ArrowDown' ? (at + 1) % items.length : (at - 1 + items.length) % items.length;
        items[next]!.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      if (restoreFocus.current && anchorRef.current instanceof HTMLElement) anchorRef.current.focus();
    };
  }, [open, anchorRef]);

  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <div
      ref={panel}
      id={id}
      role="dialog"
      aria-label={label}
      tabIndex={-1}
      className={`ui-popover${className ? ` ${className}` : ''}`}
      data-side={pos?.side ?? placement}
      style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, opacity: 0, pointerEvents: 'none' }}
    >
      {children}
    </div>,
    document.body,
  );
}
